import React from "react";
import { DeleteOutlined, EnterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAntdTheme } from "../../hooks";

interface VirtualKeyboardProps {
  /** Current input value */
  value: string;
  /** Called whenever the value changes (key press, backspace, clear) */
  onChange: (value: string) => void;
  /** Called when the user presses the submit / enter key (optional) */
  onSubmit?: () => void;
  /** Whether to show the submit (enter) key. Defaults to true. */
  showSubmit?: boolean;
  /** Whether to show the clear key. Defaults to true. */
  showClear?: boolean;
  /** Disable all keys */
  disabled?: boolean;
}

/**
 * An on-page virtual keyboard that supports a-z (lowercase only), 0-9,
 * space, and a small set of common symbols (- _ ? . / !). No capitalization.
 *
 * The keyboard fills its parent container: it uses a flex column layout
 * where each row takes an equal share of the available height, and each
 * key takes an equal share of the row width and fills the row's height.
 * The parent must give this component a bounded height (e.g. via flex: 1).
 *
 * Plain <button> elements are used instead of Ant Design's Button so the
 * keys can stretch to the full row height (AntD Button enforces a fixed
 * controlHeight that would prevent vertical filling).
 */
const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  value,
  onChange,
  onSubmit,
  showSubmit = true,
  showClear = true,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();

  const press = (ch: string) => {
    if (disabled) return;
    onChange(value + ch);
  };

  const backspace = () => {
    if (disabled) return;
    onChange(value.slice(0, -1));
  };

  const clear = () => {
    if (disabled) return;
    onChange("");
  };

  const submit = () => {
    if (disabled) return;
    onSubmit?.();
  };

  const gap = token.marginXS;

  // Base style for every key. Plain <button> so height: 100% is honored.
  const keyBaseStyle: React.CSSProperties = {
    flex: 1,
    height: "100%",
    minHeight: 128,
    minWidth: 0,
    margin: 0,
    padding: 0,
    fontSize: token.fontSizeHeading3,
    fontWeight: token.fontWeightStrong,
    borderRadius: token.borderRadius,
    border: `1px solid ${token.colorBorder}`,
    background: token.colorBgContainer,
    color: token.colorText,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: token.marginXS,
    transition: "background 0.15s, border-color 0.15s",
    userSelect: "none",
  };

  const primaryStyle: React.CSSProperties = {
    ...keyBaseStyle,
    background: token.colorPrimary,
    borderColor: token.colorPrimary,
    color: token.colorTextLightSolid,
  };

  const dangerStyle: React.CSSProperties = {
    ...keyBaseStyle,
    borderColor: token.colorError,
    color: token.colorError,
  };

  const rowStyle: React.CSSProperties = {
    display: "flex",
    flex: 1,
    gap,
    width: "100%",
    minHeight: 128,
  };

  const handleHover = (e: React.MouseEvent<HTMLButtonElement>, style: React.CSSProperties) => {
    if (disabled) return;
    e.currentTarget.style.background = token.colorBgTextHover;
  };

  const renderKey = (ch: string, flex = 1) => (
    <button
      key={ch}
      type="button"
      style={{ ...keyBaseStyle, flex }}
      disabled={disabled}
      onClick={() => press(ch)}
      onMouseEnter={(e) => handleHover(e, keyBaseStyle)}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = keyBaseStyle.background as string;
      }}
    >
      {ch}
    </button>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: 1,
        minHeight: 0,
        gap,
      }}
    >
      {/* Row 1: digits + - */}
      <div style={rowStyle}>
        {"1234567890".split("").map((c) => renderKey(c))}
        {renderKey("-")}
      </div>

      {/* Row 2: q-p + _ */}
      <div style={rowStyle}>
        {"qwertyuiop".split("").map((c) => renderKey(c))}
        {renderKey("_")}
      </div>

      {/* Row 3: a-l + ? */}
      <div style={rowStyle}>
        {"asdfghjkl".split("").map((c) => renderKey(c))}
        {renderKey("?")}
      </div>

      {/* Row 4: z-m + symbols */}
      <div style={rowStyle}>
        {"zxcvbnm".split("").map((c) => renderKey(c))}
        {renderKey(".")}
        {renderKey("/")}
        {renderKey("!")}
      </div>

      {/* Row 5: space, backspace, (optional clear, submit) */}
      <div style={rowStyle}>
        <button
          type="button"
          style={{ ...keyBaseStyle, flex: showClear || showSubmit ? 3 : 4 }}
          disabled={disabled}
          onClick={() => press(" ")}
          onMouseEnter={(e) => handleHover(e, keyBaseStyle)}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = keyBaseStyle.background as string;
          }}
        >
          {t("space")}
        </button>
        <button
          type="button"
          style={{ ...keyBaseStyle, flex: showClear || showSubmit ? 2 : 3 }}
          disabled={disabled}
          onClick={backspace}
          onMouseEnter={(e) => handleHover(e, keyBaseStyle)}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = keyBaseStyle.background as string;
          }}
        >
          {"\u232B"}
        </button>
        {showClear && (
          <button
            type="button"
            style={{ ...dangerStyle, flex: 2 }}
            disabled={disabled}
            onClick={clear}
            onMouseEnter={(e) => {
              if (disabled) return;
              e.currentTarget.style.background = token.colorErrorBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = dangerStyle.background as string;
            }}
          >
            <DeleteOutlined />
            {t("clear")}
          </button>
        )}
        {showSubmit && (
          <button
            type="button"
            style={{ ...primaryStyle, flex: 2 }}
            disabled={disabled}
            onClick={submit}
            onMouseEnter={(e) => {
              if (disabled) return;
              e.currentTarget.style.background = token.colorPrimaryHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = primaryStyle.background as string;
            }}
          >
            <EnterOutlined />
            {t("search")}
          </button>
        )}
      </div>
    </div>
  );
};

export default VirtualKeyboard;
